import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useCreateAclModule } from '../api';

export function CreateAclModulePage() {
  const navigate = useNavigate();
  const create = useCreateAclModule();

  const [parentName, setParentName] = useState('');
  const [children, setChildren] = useState<string[]>(['']);

  const addChild = () => {
    // Mirror legacy: require the last row to be filled before adding another.
    const last = children[children.length - 1] ?? '';
    if (!last.trim()) {
      toast.error('Fill the current child module before adding another.');
      return;
    }
    setChildren([...children, '']);
  };

  const removeChild = (idx: number) => {
    const next = children.filter((_, i) => i !== idx);
    setChildren(next.length ? next : ['']);
  };

  const updateChild = (idx: number, value: string) => {
    setChildren((prev) => prev.map((c, i) => (i === idx ? value : c)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!parentName.trim()) {
      toast.error('Parent module name is required.');
      return;
    }
    const filtered = children
      .map((c) => c.trim())
      .filter(Boolean)
      .map((moduleName) => ({ moduleName }));

    create.mutate(
      {
        mname: parentName.trim(),
        childModule: JSON.stringify(filtered),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Module created');
          navigate('/module');
        },
        onError: () => toast.error('Could not create the module.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="New Module"
        description="Define a parent permission key and the child actions users can be granted."
        actions={
          <Button asChild variant="outline">
            <Link to="/module">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Module details</CardTitle>
          <CardDescription>
            The parent name groups the children together in the ACL Settings
            permission table.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <div className="grid gap-1.5 sm:max-w-md">
              <Label htmlFor="parentName">
                Parent module<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="parentName"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="e.g. Manage Campaigns"
                autoFocus
              />
            </div>

            <div className="grid gap-2 sm:max-w-md">
              <Label>Child modules</Label>
              <div className="grid gap-2">
                {children.map((value, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={value}
                      onChange={(e) => updateChild(idx, e.target.value)}
                      placeholder="Child module name"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeChild(idx)}
                      aria-label="Remove child"
                      className="h-9 w-9 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addChild}
                >
                  <Plus className="h-4 w-4" />
                  Add child
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create module
              </Button>
              <Button asChild type="button" variant="ghost">
                <Link to="/module">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
